import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import {
    ContentAction,
    ContentActionView,
    ContentView,
    ProfileViewWithShadowBan,
    useDeleteReplyMutation,
    useReplyToContentMutation
} from '../../../api/client'
import { isActionApplied } from '../../content/content-card/actions'
import BaseActionButton from './BaseActionButton'

import { useState } from 'react'
import ReplyModal from '../../reply/ReplyModal'
import { toastError, toastUnretryableError } from '../../Notification'

// YouTube exposes a `canReply` flag on each comment thread. When the channel owner
// has disabled replies (or YouTube has locked the thread), the API rejects insert
// attempts with a 400. The Rust client (youtube_client::YouTubeError::ReplyNotAllowed)
// prefixes its error message with this sentinel so we can surface a specific,
// non-retryable toast instead of a generic failure.
// Keep this string in sync with services/clients/youtube_client/src/youtube_client.rs.
const YOUTUBE_REPLY_NOT_ALLOWED_SENTINEL = 'YOUTUBE_REPLY_NOT_ALLOWED'

type Props = {
    content: ContentView
    onActionClick: (contentActions: ContentActionView[], queued?: boolean) => void
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
    isMoreButton?: boolean
    disabled?: boolean
    disabledMessage?: string
}

export default function ReplyButton(props: Props) {
    const { content, onActionClick, profileCache, actionCacheMap, disabled, disabledMessage } =
        props
    const [open, setOpen] = useState(false)

    const [replyContentMutation, { loading: savingReply }] = useReplyToContentMutation({
        onError: (error) => {
            console.error('Error replying to content:', error)
            if (error.message?.includes(YOUTUBE_REPLY_NOT_ALLOWED_SENTINEL)) {
                toastUnretryableError(
                    <span>The channel owner or YouTube has disabled replies for this thread. </span>
                )
                return
            }
            toastError('Failed to reply. ')
        }
    })

    const [deleteReplyMutation, { loading: deletingReply }] = useDeleteReplyMutation({
        onError: (error) => {
            console.error('Error deleting reply:', error)
            toastError(
                'Failed to delete reply. This comment may have been deleted on the native platform or there may be a problem with authorisation. '
            )
        }
    })

    // Identify the relevant actions from the action cache map
    const allExistingContentActions = actionCacheMap.get(content.id) || []

    const actionAlreadyApplied = isActionApplied(allExistingContentActions, ContentAction.Reply)

    return (
        <>
            <ReplyModal
                open={open}
                setOpen={(open: boolean) => {
                    setOpen(open)
                }}
                content={content}
                onReply={async ({ message, suggestedReplies, actionByAccountIdOverride }) => {
                    const result = await replyContentMutation({
                        variables: {
                            message,
                            contentId: content.id,
                            suggestedReplies,
                            ...(actionByAccountIdOverride !== undefined
                                ? { actionByAccountIdOverride }
                                : {})
                        }
                    })

                    if (!!!result.errors && result.data?.replyToContent) {
                        onActionClick([result.data?.replyToContent])
                    }
                }}
                savingReply={savingReply}
                onDeleteReply={async (actionId: number) => {
                    const result = await deleteReplyMutation({
                        variables: {
                            actionId: actionId
                        }
                    })

                    if (!!!result.errors && result.data?.deleteReply) {
                        // Calling this function will update the actionCacheMap
                        // and trigger a re-render of the ContentCard so that it displays the correct state.
                        // By doing this we avoid having to refetch the entire page of content.
                        onActionClick([result.data?.deleteReply])
                    }
                }}
                deletingReply={deletingReply}
                onActionClick={onActionClick}
                profileCache={profileCache}
                actionCacheMap={actionCacheMap}
            />

            {props.isMoreButton ? (
                <button
                    className="text-blue-700 hover:text-blue-900"
                    onClick={async () => {
                        setOpen(true)
                    }}
                >
                    more
                </button>
            ) : (
                <BaseActionButton
                    tooltip="Reply"
                    // This button, instead of taking an immediate action, just pens a modal
                    // As such there is no need to display a loading indicator around the button
                    loading={false}
                    colour="blue"
                    actionAlreadyApplied={actionAlreadyApplied}
                    icon={<ChatBubbleLeftRightIcon />}
                    onActionClick={async () => {
                        setOpen(true)
                    }}
                    isActionQueued={false}
                    disabled={disabled}
                    disabledMessage={disabledMessage}
                />
            )}
        </>
    )
}
